import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';

import Axios from '../../Assets/Axios/Axios';

import { useToastContext } from '../../Context/ToastContext';

import Layout from './Components/Layout';

import { toCurrencyString } from '../../Helpers';

import { uploadFile, UPLOAD_FOLDERS } from '../../Services/S3';

import { ProfessionalServices, GetCategories } from '../../Server/Query';
import {
  createService,
  ProSetServiceActive,
  ProCreateServicePackage,
  ProUpdateServicePackage,
  ProDeleteServicePackage,
} from '../../Server/Mutation';

const EMPTY_SERVICE = {
  categoryId: '',
  hotlineNumber: '',
  location: '',
  websiteUrl: '',
  description: '',
};
const EMPTY_PACKAGE = {
  id: null,
  serviceId: null,
  name: '',
  price: '',
  features: '',
  description: '',
  isActive: true,
};

function Service() {
  const { showToastMessage } = useToastContext();

  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [serviceForm, setServiceForm] = useState(null);
  const [packageForm, setPackageForm] = useState(null);
  const [deletePackage, setDeletePackage] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchServices = useCallback(async () => {
    const { data, errors } = await Axios(ProfessionalServices);
    if (errors || !data?.professionalServices) {
      showToastMessage('Failed to load your services.', 'error');
      return;
    }
    setServices(data.professionalServices);
  }, [showToastMessage]);

  useEffect(() => {
    fetchServices();
    Axios(GetCategories).then(({ data, errors }) => {
      if (errors || !data?.categories) return;
      setCategories(data.categories);
    });
  }, [fetchServices]);

  // Categories the professional already has a service under — excluded when
  // adding a new service (one service per category).
  const usedCategoryIds = useMemo(
    () => new Set(services.map(s => s.categoryId).filter(Boolean)),
    [services]
  );
  const availableCategories = useMemo(
    () => categories.filter(c => !usedCategoryIds.has(c.id)),
    [categories, usedCategoryIds]
  );

  async function handleAddService() {
    const category = categories.find(c => c.id === serviceForm.categoryId);
    if (!category) {
      showToastMessage('Please select a category.', 'error');
      return;
    }
    if (!serviceForm.hotlineNumber.trim()) {
      showToastMessage('Hotline number is required.', 'error');
      return;
    }
    setBusy(true);
    try {
      // Upload any picked images to S3 first; persist the returned URLs.
      const [icon, bannerImage] = await Promise.all([
        serviceForm.iconFile
          ? uploadFile(serviceForm.iconFile, UPLOAD_FOLDERS.SERVICE_ICON)
          : null,
        serviceForm.bannerFile
          ? uploadFile(serviceForm.bannerFile, UPLOAD_FOLDERS.SERVICE_BANNER)
          : null,
      ]);

      const { errors } = await Axios(
        createService({
          name: category.name,
          categoryId: category.id,
          hotlineNumber: serviceForm.hotlineNumber,
          location: serviceForm.location,
          websiteUrl: serviceForm.websiteUrl || null,
          description: serviceForm.description,
          icon,
          bannerImage,
        })
      );
      if (errors) {
        showToastMessage(errors[0]?.message ?? 'Failed to add service.', 'error');
        return;
      }
      showToastMessage('Service added. Add packages, then activate it.', 'success');
      setServiceForm(null);
      await fetchServices();
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleActive(service) {
    const { errors } = await Axios(
      ProSetServiceActive({ id: service.id, isActive: !service.isActive })
    );
    if (errors) {
      // Backend blocks activation without an active package.
      showToastMessage(errors[0]?.message ?? 'Failed to update service.', 'error');
      return;
    }
    await fetchServices();
  }

  async function handleSavePackage() {
    if (!packageForm.name.trim() || packageForm.price === '') {
      showToastMessage('Package name and price are required.', 'error');
      return;
    }
    const features = packageForm.features
      .split(',')
      .map(f => f.trim())
      .filter(Boolean);

    setBusy(true);
    try {
      const payload = {
        name: packageForm.name.trim(),
        price: packageForm.price,
        description: packageForm.description || null,
        features,
        isActive: packageForm.isActive,
      };
      const { errors } = await Axios(
        packageForm.id
          ? ProUpdateServicePackage({ id: packageForm.id, ...payload })
          : ProCreateServicePackage({ serviceId: packageForm.serviceId, ...payload })
      );
      if (errors) {
        showToastMessage(errors[0]?.message ?? 'Failed to save package.', 'error');
        return;
      }
      showToastMessage(`Package ${packageForm.id ? 'updated' : 'added'}.`, 'success');
      setPackageForm(null);
      await fetchServices();
    } finally {
      setBusy(false);
    }
  }

  async function handleDeletePackage() {
    const target = deletePackage;
    setDeletePackage(null);
    const { errors } = await Axios(ProDeleteServicePackage({ id: target.id }));
    if (errors) {
      showToastMessage(errors[0]?.message ?? 'Failed to delete package.', 'error');
      return;
    }
    showToastMessage('Package deleted.', 'success');
    await fetchServices();
  }

  return (
    <Layout message='Manage your services and packages' isAvatarButtonsAvailable={false}>
      <Box maxWidth={760}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
          <Typography variant='h6' fontWeight={600}>
            My Services
          </Typography>
          <Tooltip
            title={
              availableCategories.length === 0
                ? 'You already have a service in every category'
                : 'Add a new service'
            }
          >
            <span>
              <Button
                variant='contained'
                startIcon={<AddIcon />}
                disabled={availableCategories.length === 0}
                onClick={() => setServiceForm({ ...EMPTY_SERVICE })}
              >
                Add Service
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {services.length === 0 && (
          <Typography color='text.secondary'>
            You don&apos;t have any services yet. Add one to get started.
          </Typography>
        )}

        <Stack spacing={3}>
          {services.map(service => (
            <Paper key={service.id} variant='outlined' sx={{ p: 2 }}>
              <Stack direction='row' alignItems='center' justifyContent='space-between'>
                <Box>
                  <Chip
                    size='small'
                    label={service.category?.name ?? 'Uncategorised'}
                    color='primary'
                    variant='outlined'
                    sx={{ mb: 0.5 }}
                  />
                  <Typography variant='subtitle1' fontWeight={600}>
                    {service.name}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={service.isActive}
                      onChange={() => handleToggleActive(service)}
                    />
                  }
                  label={service.isActive ? 'Active' : 'Inactive'}
                />
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Stack direction='row' justifyContent='space-between' alignItems='center' mb={1}>
                <Typography fontWeight={600}>Package Details</Typography>
                <Button
                  size='small'
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setPackageForm({ ...EMPTY_PACKAGE, serviceId: service.id })
                  }
                >
                  Add Package
                </Button>
              </Stack>

              {service.packages.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  No packages yet. Add at least one to activate this service.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {service.packages.map(pkg => (
                    <Box
                      key={pkg.id}
                      sx={{ p: 1.5, borderRadius: 1, bgcolor: 'grey.50' }}
                    >
                      <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
                        <Box>
                          <Typography fontWeight={600}>
                            {pkg.name} — {toCurrencyString(pkg.price)}
                            {!pkg.isActive && (
                              <Chip label='Inactive' size='small' sx={{ ml: 1 }} />
                            )}
                          </Typography>
                          {pkg.description && (
                            <Typography variant='body2' color='text.secondary'>
                              {pkg.description}
                            </Typography>
                          )}
                          {(pkg.features ?? []).length > 0 && (
                            <Stack spacing={0.25} mt={0.5}>
                              {pkg.features.map((f, i) => (
                                <Stack key={i} direction='row' alignItems='center' gap={0.5}>
                                  <CheckCircleIcon fontSize='small' color='success' />
                                  <Typography variant='body2'>{f}</Typography>
                                </Stack>
                              ))}
                            </Stack>
                          )}
                        </Box>
                        <Box>
                          <IconButton
                            size='small'
                            onClick={() =>
                              setPackageForm({
                                id: pkg.id,
                                serviceId: service.id,
                                name: pkg.name,
                                price: pkg.price,
                                features: (pkg.features ?? []).join(', '),
                                description: pkg.description ?? '',
                                isActive: pkg.isActive,
                              })
                            }
                          >
                            <EditIcon fontSize='small' />
                          </IconButton>
                          <IconButton
                            size='small'
                            color='error'
                            onClick={() => setDeletePackage(pkg)}
                          >
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          ))}
        </Stack>
      </Box>

      {/* Add service dialog */}
      <Dialog open={Boolean(serviceForm)} onClose={() => setServiceForm(null)} fullWidth maxWidth='sm'>
        <DialogTitle>Add Service</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              label='Category'
              required
              value={serviceForm?.categoryId ?? ''}
              onChange={e => setServiceForm(f => ({ ...f, categoryId: e.target.value }))}
              helperText='You can only have one service per category.'
            >
              {availableCategories.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label='Hotline number'
              required
              value={serviceForm?.hotlineNumber ?? ''}
              onChange={e => setServiceForm(f => ({ ...f, hotlineNumber: e.target.value }))}
            />
            <TextField
              label='Location'
              value={serviceForm?.location ?? ''}
              onChange={e => setServiceForm(f => ({ ...f, location: e.target.value }))}
            />
            <TextField
              label='Website'
              value={serviceForm?.websiteUrl ?? ''}
              onChange={e => setServiceForm(f => ({ ...f, websiteUrl: e.target.value }))}
            />
            <TextField
              label='Description'
              multiline
              minRows={2}
              value={serviceForm?.description ?? ''}
              onChange={e => setServiceForm(f => ({ ...f, description: e.target.value }))}
            />
            <Stack direction='row' spacing={2}>
              <Button variant='outlined' component='label' size='small'>
                {serviceForm?.iconFile ? 'Icon ✓' : 'Upload icon'}
                <input
                  hidden
                  type='file'
                  accept='image/*'
                  onChange={e =>
                    setServiceForm(f => ({ ...f, iconFile: e.target.files?.[0] ?? null }))
                  }
                />
              </Button>
              <Button variant='outlined' component='label' size='small'>
                {serviceForm?.bannerFile ? 'Banner ✓' : 'Upload banner'}
                <input
                  hidden
                  type='file'
                  accept='image/*'
                  onChange={e =>
                    setServiceForm(f => ({ ...f, bannerFile: e.target.files?.[0] ?? null }))
                  }
                />
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServiceForm(null)}>Cancel</Button>
          <Button variant='contained' onClick={handleAddService} disabled={busy}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / edit package dialog */}
      <Dialog open={Boolean(packageForm)} onClose={() => setPackageForm(null)} fullWidth maxWidth='sm'>
        <DialogTitle>{packageForm?.id ? 'Edit Package' : 'Add Package'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label='Name'
              required
              placeholder='e.g. Standard, Premium'
              value={packageForm?.name ?? ''}
              onChange={e => setPackageForm(f => ({ ...f, name: e.target.value }))}
            />
            <TextField
              label='Price'
              type='number'
              required
              value={packageForm?.price ?? ''}
              onChange={e => setPackageForm(f => ({ ...f, price: e.target.value }))}
            />
            <TextField
              label='Features'
              placeholder='Comma-separated, e.g. Consultation, On-site service'
              helperText='Separate each feature with a comma.'
              value={packageForm?.features ?? ''}
              onChange={e => setPackageForm(f => ({ ...f, features: e.target.value }))}
            />
            <TextField
              label='Description'
              multiline
              minRows={2}
              value={packageForm?.description ?? ''}
              onChange={e => setPackageForm(f => ({ ...f, description: e.target.value }))}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={packageForm?.isActive ?? true}
                  onChange={e => setPackageForm(f => ({ ...f, isActive: e.target.checked }))}
                />
              }
              label='Active'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackageForm(null)}>Cancel</Button>
          <Button variant='contained' onClick={handleSavePackage} disabled={busy}>
            {packageForm?.id ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete package confirmation */}
      <Dialog open={Boolean(deletePackage)} onClose={() => setDeletePackage(null)}>
        <DialogTitle>Delete package</DialogTitle>
        <DialogContent>
          <Typography>
            Delete &quot;{deletePackage?.name}&quot;? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePackage(null)}>Cancel</Button>
          <Button color='error' variant='contained' onClick={handleDeletePackage}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default Service;
